'use client';

import { useClientConfig } from '@spikemark/core';
import classNames from 'classnames';
import { AnimatePresence, motion as m } from 'framer-motion';
import { ImageIcon } from 'lucide-react';
import Image, { ImageLoaderProps } from 'next/image';
import { FC, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
type MascotVariant = 'tiny' | 'xSmall' | 'sm' | 'small' | 'print' | 'badge' | 'medium' | 'large' | 'xlarge' | 'xxlarge' | 'input' | 'cluster';

// Only display the loading animation if we have been loading for this amount of
// time. If we load the image quickly - likely cached - then we don't need to show
// the animation.
const LOADING_ANIMATION_START_DELAY_MS = 200;
export const variants: Record<MascotVariant, {
  width: number;
  height: number;
  border: boolean;
  roundedClass?: 'rounded' | 'rounded-md' | 'rounded-lg' | 'rounded-xl' | 'rounded-full';
}> = {
  tiny: {
    width: 16,
    height: 16,
    border: false,
    roundedClass: 'rounded'
  },
  xSmall: {
    width: 24,
    height: 24,
    border: false,
    roundedClass: 'rounded'
  },
  sm: {
    width: 26,
    height: 26,
    border: false
  },
  small: {
    width: 32,
    height: 32,
    border: false,
    roundedClass: 'rounded-md'
  },
  badge: {
    width: 40,
    height: 40,
    border: false,
    roundedClass: 'rounded-md'
  },
  print: {
    width: 48,
    height: 48,
    border: false,
    roundedClass: 'rounded-md'
  },
  medium: {
    width: 76,
    height: 76,
    border: true,
    roundedClass: 'rounded-lg'
  },
  large: {
    width: 90,
    height: 90,
    border: true,
    roundedClass: 'rounded-lg'
  },
  xlarge: {
    width: 120,
    height: 120,
    border: true,
    roundedClass: 'rounded-xl'
  },
  xxlarge: {
    width: 200,
    height: 200,
    border: true,
    roundedClass: 'rounded-xl'
  },
  input: {
    width: 100,
    height: 100,
    border: true,
    roundedClass: 'rounded-xl'
  },
  cluster: {
    width: 60,
    height: 60,
    border: true,
    roundedClass: 'rounded-full'
  }
};
export type MascotProps = {
  path?: string;
  variant: MascotVariant;
  displayMode?: 'team' | 'player';
  alt: string;
  priority?: boolean;
  className?: string;
  border?: boolean;
  borderColor?: string;
  rounded?: boolean;
};
export const Mascot: FC<MascotProps> = memo(({
  path,
  variant: variantName,
  alt,
  displayMode = 'team',
  className,
  border: propBorder,
  borderColor,
  rounded = true
}: MascotProps) => {
  const {
    cloudinary
  } = useClientConfig();
  const teamLoader = useCallback(() => ({
    src,
    width,
    quality
  }: ImageLoaderProps) => {
    return `${cloudinary.basePath}${src}?tx=w_${width},ar_1,q_${quality},c_fit`;
  }, [cloudinary]);
  const playerLoader = useCallback(() => ({
    src,
    width,
    quality
  }: ImageLoaderProps) => {
    return `${cloudinary.basePath}${src}?tx=w_${width},ar_1,q_${quality},g_face:center,c_thumb,z_0.85`;
  }, [cloudinary]);
  const variant = variants[variantName];
  const border = propBorder || true;
  const padding = displayMode === 'team' ? Math.round(variant.width * 0.1) : 0;
  const [hasError, setHasError] = useState<boolean>(false);
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPath, setCurrentPath] = useState<string | undefined>(path);
  const {
    width,
    height
  } = variant;
  const roundedClass = rounded ? variant.roundedClass : undefined;
  const contentWidth = width - padding * 2;
  const contentHeight = height - padding * 2;
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitializedRef = useRef<boolean>(false);
  const handleComplete = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsLoading(false);
    setHasLoaded(true);
  }, []);
  const handleLoadStart = useCallback(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Set up the delayed loading animation
    timeoutRef.current = setTimeout(() => {
      setIsLoading(true);
    }, LOADING_ANIMATION_START_DELAY_MS);
  }, []);
  const handleLoad = useCallback(() => {
    handleComplete();
  }, [handleComplete]);
  const handleError = useCallback(() => {
    handleComplete();
    setHasError(true);
  }, [handleComplete]);
  const loader = useMemo(() => {
    const loaderFactory = displayMode === 'team' ? teamLoader : playerLoader;
    return loaderFactory();
  }, [displayMode, playerLoader, teamLoader]);

  // Reset state when path changes
  useEffect(() => {
    if (path !== currentPath) {
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setCurrentPath(path);
      setHasLoaded(false);
      setHasError(false);
      setIsLoading(false);
      isInitializedRef.current = false;
    }
  }, [path, currentPath]);

  // Start loading process when we have a path and haven't initialized yet
  useLayoutEffect(() => {
    if (path && !hasLoaded && !hasError && !isInitializedRef.current) {
      isInitializedRef.current = true;
      handleLoadStart();
    }
  }, [path, hasLoaded, hasError, handleLoadStart]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);
  return <div className={classNames(className, 'overflow-clip bg-white justify-self-center', {
    'border border-gray-300 box-content': border,
    border: variantName === 'tiny',
    'border-none border-transparent': !border
  }, roundedClass)} data-variant={variantName} style={{
    borderColor
  }}>
        <div className={classNames('relative', {
      'bg-white flex items-center justify-center': border
    })} style={{
      width,
      height
    }}>
          {path && !hasError ? <Image data-mascotimage key={path} alt={alt} src={path} loader={loader} quality={90} width={displayMode === 'player' ? Math.ceil(contentWidth * 1.5) : contentWidth} height={displayMode === 'player' ? Math.ceil(contentHeight * 1.5) : contentHeight} style={{
        width: contentWidth,
        height: contentHeight,
        minWidth: contentWidth
      }} className={classNames('shrink-0', {
        'object-center object-contain': displayMode === 'team',
        'object-center object-cover': displayMode === 'player',
        'opacity-0': isLoading,
        'opacity-100': !isLoading
      })} onError={handleError} onLoad={handleLoad} /> : <>
              {displayMode === 'player' ? <svg width={contentWidth} height={contentHeight} viewBox="0 0 88 88" className="bg-white">
                  <rect width="88" height="88" fill="#E2E8F0" />
                  <circle cx="44" cy="31.625" r="18.625" fill="#94A3B8" />
                  <g clipPath="url(#clip0_521_14361)">
                    <circle cx="43.9751" cy="91.4375" r="38.1875" fill="#94A3B8" />
                  </g>
                  <defs>
                    <clipPath id="clip0_521_14361">
                      <rect width="76" height="27" fill="white" transform="translate(6 53)" />
                    </clipPath>
                  </defs>
                </svg> : <div style={{
          width: contentWidth,
          height: contentHeight
        }} className="bg-gray-300/50 hover:bg-gray-300 flex items-center justify-center text-gray-600 text-light">
                  <ImageIcon width={contentHeight * 0.35} height={contentHeight * 0.35} />
                </div>}
            </>}
          <AnimatePresence>
            {path && isLoading && !hasError ? <m.div className={classNames('absolute inset-0 overflow-hidden', roundedClass)} transition={{
          duration: 0.3,
          ease: 'easeOut'
        }} exit={{
          opacity: 0
        }}>
                <m.div className="absolute top-0 w-full bg-gray-300" initial={{
            top: -20,
            height: 20
          }} animate={{
            top: height - 3,
            height: 6
          }} transition={{
            repeat: Infinity,
            repeatType: 'reverse',
            duration: 1,
            ease: [0.86, 0, 0.07, 1]
          }} />
                <div className={classNames('absolute inset-0.5 bg-gray-100', roundedClass)} />
              </m.div> : null}
          </AnimatePresence>
        </div>
      </div>;
});